import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { Box, CircularProgress, Alert } from '@mui/material';
import { AnalyticsDataPoint, PuzzleOpensDataPoint, PuzzleSolvesDataPoint, AnalyticsResponse } from './types';
import { PUZZLE_CONFIGS } from './constants';
import { TimeRangeSelector } from './components/TimeRangeSelector';
import { StatsSummary } from './components/StatsSummary';
import { VisitorChart } from './components/VisitorChart';
import { PuzzleChart } from './components/PuzzleChart';
import './AdminAnalytics.less';

const AdminAnalytics = () => {
  const [data, setData] = useState<AnalyticsDataPoint[]>([]);
  const [puzzleOpens, setPuzzleOpens] = useState<PuzzleOpensDataPoint[]>([]);
  const [puzzleSolves, setPuzzleSolves] = useState<PuzzleSolvesDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<number>(30);
  const [hoveredOpens, setHoveredOpens] = useState<string | null>(null);
  const [hoveredSolves, setHoveredSolves] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setError('You must be logged in to view analytics');
        setLoading(false);
        return;
      }

      const token = await user.getIdToken();

      const response = await axios.get<AnalyticsResponse>(`/api/analytics?days=${days}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setData(response.data.data);
      setPuzzleOpens(response.data.puzzleOpens);
      setPuzzleSolves(response.data.puzzleSolves);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      if (err.response?.status === 403) {
        setError('Unauthorized - You do not have admin access');
      } else {
        setError('Failed to load analytics data');
      }
      setLoading(false);
    }
  };

  const handleDaysChange = (event: React.MouseEvent<HTMLElement>, newDays: number | null) => {
    if (newDays !== null) {
      setDays(newDays);
    }
  };

  // Format date for display (MM/DD)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Calculate summary stats for today (most recent date in the data)
  const today = data.length > 0 ? data[data.length - 1] : null;
  const todayVisitors = today?.totalCount || 0;
  const todayLoggedIn = today?.loggedInCount || 0;
  const todayLoggedOut = today?.loggedOutCount || 0;

  // Calculate total puzzle opens and solves for today
  const todayPuzzleOpensData = puzzleOpens.length > 0 ? puzzleOpens[puzzleOpens.length - 1] : null;
  const todayPuzzleSolvesData = puzzleSolves.length > 0 ? puzzleSolves[puzzleSolves.length - 1] : null;

  const todayOpens = todayPuzzleOpensData
    ? Object.keys(todayPuzzleOpensData).filter(k => k !== 'date').reduce((sum, key) => sum + (Number(todayPuzzleOpensData[key]) || 0), 0)
    : 0;
  const todaySolves = todayPuzzleSolvesData
    ? Object.keys(todayPuzzleSolvesData).filter(k => k !== 'date').reduce((sum, key) => sum + (Number(todayPuzzleSolvesData[key]) || 0), 0)
    : 0;

  return (
    <Box className="admin-analytics-container">
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 200px)">
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 200px)">
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            {error}
          </Alert>
        </Box>
      )}

      {!loading && !error && data.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 200px)">
          <Alert severity="info">No analytics data available yet</Alert>
        </Box>
      )}

      {!loading && !error && data.length > 0 && (
        <Box pt={4}>
          <TimeRangeSelector value={days} onChange={handleDaysChange} />

          <StatsSummary
            visitors={todayVisitors}
            loggedIn={todayLoggedIn}
            loggedOut={todayLoggedOut}
            puzzleOpens={todayOpens}
            puzzleSolves={todaySolves}
          />

          <VisitorChart data={data} formatDate={formatDate} />

          <PuzzleChart
            title="Puzzle Opens"
            data={puzzleOpens}
            puzzleConfigs={PUZZLE_CONFIGS}
            formatDate={formatDate}
            hoveredPuzzle={hoveredOpens}
            onHover={setHoveredOpens}
            onLeave={() => setHoveredOpens(null)}
          />

          <PuzzleChart
            title="Puzzle Solves"
            data={puzzleSolves}
            puzzleConfigs={PUZZLE_CONFIGS}
            formatDate={formatDate}
            hoveredPuzzle={hoveredSolves}
            onHover={setHoveredSolves}
            onLeave={() => setHoveredSolves(null)}
          />
        </Box>
      )}
    </Box>
  );
};

export default AdminAnalytics;
